import { useState } from "react";
import { Smile } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

// Curated emoji set grouped so users don't hunt through 3000 unicode glyphs.
const EMOJI_GROUPS = [
  {
    name: "Smileys",
    emojis: ["😀","😃","😄","😁","😆","😅","🤣","😂","🙂","🙃","😉","😊","😇","🥰","😍","🤩","😘","😗","😙","😚","😋","😛","😜","🤪","😝","🤑","🤗","🤭","🤫","🤔","🫡","🫢","🫣","😐","😑","😶","😏","😒","🙄","😬","🤥","😴","🤤","😪","😮‍💨","🤒","🤕","🤧","😵","🥴","🤠","🥳","🥸","😎"],
  },
  {
    name: "Reactions",
    emojis: ["👍","👎","👏","🙌","👐","🤝","🙏","💪","🫶","🤗","🎉","🎊","🔥","💯","✨","⭐","🌟","💥","💫","💢","💤","💦","💨","🕐","⏰","✅","☑️","❌","⚠️","⛔","🚫","❗","❓","💡","📌","📍","🔔"],
  },
  {
    name: "Body & Health",
    emojis: ["💉","🩸","🩹","🩺","💊","🧬","🧪","🩻","🫀","🫁","🧠","👀","👁️","👂","👃","🦴","🦾","🏋️","🏃","🚴","🧘","🥇","🥈","🥉","🎯","📈","📉","📊","📅","📆","⏱️","⏳","🌡️","💧"],
  },
  {
    name: "Objects",
    emojis: ["🧴","🧼","🪥","🧻","🚿","🛁","💤","🛌","🌙","☀️","⚡","💧","🔬","🥛","🍳","🥗","🥑","🍎","🍊","🍋","🍌","🍇","🥩","🥚","🥜","🥤","☕","🍵","🧊","🥃","🍺","🍷"],
  },
  {
    name: "Hearts & Symbols",
    emojis: ["❤️","🧡","💛","💚","💙","💜","🖤","🤍","🤎","💔","❤️‍🔥","💕","💞","💓","💗","💖","💘","💝","♻️","⚕️","🔒","🔓","📎","🔗","➡️","⬅️","⬆️","⬇️","▶️","⏸️","⏹️","💠"],
  },
];

// Insert `emoji` at the current cursor position of `ref.current` (an <input> or <textarea>) and update via setter.
export function insertAtCursor(el, value, emoji, setValue) {
  if (!el) { setValue((value || "") + emoji); return; }
  const start = el.selectionStart ?? value.length;
  const end = el.selectionEnd ?? value.length;
  const next = value.slice(0, start) + emoji + value.slice(end);
  setValue(next);
  // Restore caret after React re-renders
  requestAnimationFrame(() => {
    try {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    } catch { /* ignore */ }
  });
}

export default function EmojiPickerButton({ onPick, className = "", size = "sm", testid = "emoji-picker" }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`h-8 w-8 rounded-none border border-slate-200 bg-slate-50 hover:bg-slate-200 text-slate-600 hover:text-[#0D9488] transition-colors inline-flex items-center justify-center ${className}`}
          data-testid={testid}
          title="Add emoji"
          aria-label="Add emoji"
        >
          <Smile className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="top"
        className="w-80 p-0 bg-white border border-slate-200 rounded-none max-h-80 overflow-y-auto"
        data-testid="emoji-picker-panel"
      >
        <div className="p-3 space-y-3">
          {EMOJI_GROUPS.map((g) => (
            <div key={g.name}>
              <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">// {g.name}</div>
              <div className="grid grid-cols-10 gap-0.5">
                {g.emojis.map((e, i) => (
                  <button
                    key={`${g.name}-${i}`}
                    type="button"
                    onClick={() => { onPick?.(e); }}
                    className="w-7 h-7 flex items-center justify-center hover:bg-slate-200 text-lg rounded-none"
                    aria-label={`insert ${e}`}
                    data-testid={`emoji-${e}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
